import React, { useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAppSelector, useAppDispatch } from '@/store/hooks'
import { setRedirectUrl } from '@/store/slices/authSlice'
import { UserRole, RoleRequirement, checkRoleRequirement } from '@/utils/roleUtils'
import { ROUTES, PUBLIC_ROUTES } from '@/constants/routes'

interface ProtectedRouteProps {
  children: React.ReactNode
  // Backward compatibility - single role requirement
  requiredRole?: UserRole
  // New flexible role requirements
  roleRequirement?: RoleRequirement
  // Allow unassigned users (for pending approval page)
  allowUnassigned?: boolean
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole,
  roleRequirement,
  allowUnassigned = false
}) => {
  const location = useLocation()
  const dispatch = useAppDispatch()
  const { isAuthenticated, user, isLoading, token } = useAppSelector((state) => state.auth)

  // Store the attempted URL for redirect after login
  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      const redirectUrl = location.pathname + location.search
      dispatch(setRedirectUrl(redirectUrl))
    }
  }, [isAuthenticated, isLoading, location.pathname, location.search, dispatch])

  // Helper function to check if user has required role(s)
  const hasRequiredRole = (): boolean => {
    const userRole = user?.role as UserRole | undefined

    // Special handling for unassigned users
    if (allowUnassigned && userRole === 'unassigned') {
      return true
    }

    // Block unassigned users from accessing protected routes (unless explicitly allowed)
    if (userRole === 'unassigned' && !allowUnassigned) {
      return false
    }

    // Backward compatibility - check single required role
    if (requiredRole) {
      return userRole === requiredRole
    }

    // No role requirement means everyone can access (except unassigned unless explicitly allowed)
    if (!roleRequirement) return true

    // Use the utility function for role checking
    return checkRoleRequirement(userRole, roleRequirement)
  }

  // Helper function to get role requirement description for error message
  const getRoleRequirementDescription = (): string => {
    if (requiredRole) {
      return requiredRole
    }

    if (!roleRequirement) return 'authenticated user'

    if (roleRequirement.role) {
      return roleRequirement.role
    }

    if (roleRequirement.anyOf) {
      return `any of: ${roleRequirement.anyOf.join(', ')}`
    }

    if (roleRequirement.allOf) {
      return `all of: ${roleRequirement.allOf.join(', ')}`
    }

    if (roleRequirement.customCheck) {
      return 'custom role requirement'
    }

    return 'valid role'
  }

  // Show loading state while checking auth
  // Show loading if:
  // 1. Currently loading with no user data, OR
  // 2. We have a token but no user (OAuth callback state transition), OR
  // 3. We're loading and the auth state might be transitioning
  if (isLoading || (token && !user && !isAuthenticated)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    )
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />
  }

  // Check role-based access
  if (!hasRequiredRole()) {
    // Special handling for unassigned users - redirect to pending page unless explicitly allowed
    if ((user?.role as string) === 'unassigned' && !allowUnassigned) {
      return <Navigate to={PUBLIC_ROUTES.PENDING} replace />
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center p-8">
          <h1 className="text-2xl font-bold text-foreground mb-4">Access Denied</h1>
          <p className="text-muted-foreground mb-6">
            You don't have permission to access this page.
          </p>
          <p className="text-sm text-muted-foreground">
            Required: <span className="font-medium">{getRoleRequirementDescription()}</span>
          </p>
          <p className="text-sm text-muted-foreground">
            Your role: <span className="font-medium">{user?.role}</span>
          </p>
        </div>
      </div>
    )
  }

  return <>{children}</>
} 